import { ReactElement, useEffect } from 'react';
import {
  Stack,
  InputAdornment,
  TextField,
  Typography,
  Avatar,
  Box,
  FormControlLabel,
  Switch,
  createFilterOptions,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVectorSquare as LogisticsZonesIcon } from '@fortawesome/free-solid-svg-icons';

import {
  Subscription,
  CustomAutocomplete,
  UserInfo,
  AccessFeature,
  LogisiticZoneInfo,
} from '~components/index';
import { RoutesActionFilterChip } from '~modules/Routes/styles';
import { getDefaultAvatar } from '~utils/user';
import {
  getSuffixCapacity,
  getDescriptionCapacity,
} from '~utils/capacityTypes';
import { PlanFeaturesTypes, Roles } from '~globals/types/enums';
import { useCurrentCapacityTypeInfo } from '~hooks/useCurrentCapacityTypeInfo';
// import { IN_WAREHOUSE_STATE_OPTIONS } from '~constants/ordersStatesOptions';
import { PICKING_STATE_OPTIONS } from '~constants/ordersStatesOptions';
import { SOURCE_IMPORT_TYPES_OPTIONS } from '~constants/sourceImportType';

import { useOrdersFiltersMenuV2Context } from '../../Context';

import { OrdersFiltersMenuV2Inputs } from '../../styles';

const OrdersFiltersMenuV2VariantInWarehouse = (): ReactElement => {
  const {
    filters: { inWarehouse: filtersInWarehouse },
    handleChangeInputFilter,
    handleChangeInputDateFilter,
    selectsData,
  } = useOrdersFiltersMenuV2Context();

  const { unit1CapacityTypeId, unit2CapacityTypeId } =
    useCurrentCapacityTypeInfo();

  // useEffect(() => {
  //   if (filtersInWarehouse.activeFilters.itemState?.length) {
  //     filtersInWarehouse.autocompletesRefs.itemState.current?.setValue(
  //       filtersInWarehouse.activeFilters.itemState,
  //     );
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [filtersInWarehouse.activeFilters.itemState]);

  useEffect(() => {
    if (filtersInWarehouse.activeFilters.services?.length) {
      filtersInWarehouse.autocompletesRefs.services.current?.setValue(
        filtersInWarehouse.activeFilters.services,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInWarehouse.activeFilters.services]);

  useEffect(() => {
    if (filtersInWarehouse.activeFilters.zones?.length) {
      filtersInWarehouse.autocompletesRefs.logisticsZones.current?.setValue(
        filtersInWarehouse.activeFilters.zones,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInWarehouse.activeFilters.zones]);

  useEffect(() => {
    if (filtersInWarehouse.activeFilters.assignedUser) {
      filtersInWarehouse.autocompletesRefs.assignedUser.current?.setValue(
        filtersInWarehouse.activeFilters.assignedUser,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInWarehouse.activeFilters.assignedUser]);

  useEffect(() => {
    if (filtersInWarehouse.activeFilters.pickingState) {
      filtersInWarehouse.autocompletesRefs.pickingState.current?.setValue(
        filtersInWarehouse.activeFilters.pickingState,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInWarehouse.activeFilters.pickingState]);

  useEffect(() => {
    if (filtersInWarehouse.activeFilters.sourceImports?.length) {
      filtersInWarehouse.autocompletesRefs.sourceImports.current?.setValue(
        filtersInWarehouse.activeFilters.sourceImports,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInWarehouse.activeFilters.sourceImports]);

  return (
    <OrdersFiltersMenuV2Inputs>
      <Stack spacing={0.5}>
        <AccessFeature.Typograghy
          validFeature={PlanFeaturesTypes.LimitNotPro}
          variant="caption"
          color="text.secondary"
        >
          &#8288;{getDescriptionCapacity(unit1CapacityTypeId)}
        </AccessFeature.Typograghy>

        <Stack direction="row" spacing={1} alignItems="center">
          <AccessFeature.TextField
            validFeature={PlanFeaturesTypes.LimitNotPro}
            showChip={false}
            label="Mayor a"
            name="unit1Gt"
            value={filtersInWarehouse.filters.unit1Gt ?? ''}
            onChange={handleChangeInputFilter(filtersInWarehouse)}
            type="number"
            onKeyDown={(event) => event.stopPropagation()}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {getSuffixCapacity(unit1CapacityTypeId)}
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <AccessFeature.TextField
            validFeature={PlanFeaturesTypes.LimitNotPro}
            showChip={false}
            label="Menor a"
            name="unit1Lt"
            value={filtersInWarehouse.filters.unit1Lt ?? ''}
            onChange={handleChangeInputFilter(filtersInWarehouse)}
            type="number"
            onKeyDown={(event) => event.stopPropagation()}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {getSuffixCapacity(unit1CapacityTypeId)}
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <AccessFeature.Typograghy
          validFeature={PlanFeaturesTypes.LimitNotPro}
          variant="caption"
          color="text.secondary"
        >
          &#8288;{getDescriptionCapacity(unit2CapacityTypeId)}
        </AccessFeature.Typograghy>

        <Stack direction="row" spacing={1} alignItems="center">
          <AccessFeature.TextField
            validFeature={PlanFeaturesTypes.LimitNotPro}
            showChip={false}
            label="Mayor a"
            name="unit2Gt"
            value={filtersInWarehouse.filters.unit2Gt ?? ''}
            onChange={handleChangeInputFilter(filtersInWarehouse)}
            type="number"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {getSuffixCapacity(unit2CapacityTypeId)}
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <AccessFeature.TextField
            validFeature={PlanFeaturesTypes.LimitNotPro}
            showChip={false}
            label="Menor a"
            name="unit2Lt"
            value={filtersInWarehouse.filters.unit2Lt ?? ''}
            onChange={handleChangeInputFilter(filtersInWarehouse)}
            type="number"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {getSuffixCapacity(unit2CapacityTypeId)}
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          &#8288;Precio
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Mayor a"
            name="priceGt"
            value={filtersInWarehouse.filters.priceGt ?? ''}
            onChange={handleChangeInputFilter(filtersInWarehouse)}
            type="number"
            onKeyDown={(event) => event.stopPropagation()}
            fullWidth
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <TextField
            label="Menor a"
            name="priceLt"
            value={filtersInWarehouse.filters.priceLt ?? ''}
            onChange={handleChangeInputFilter(filtersInWarehouse)}
            type="number"
            onKeyDown={(event) => event.stopPropagation()}
            fullWidth
          />
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Stack spacing={0.5} direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            &#8288;Fecha de vencimiento
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={
                  filtersInWarehouse.filters.includeWithoutExpirationDate ??
                  false
                }
                onChange={(event) =>
                  filtersInWarehouse.onChangeFilterInput(
                    'includeWithoutExpirationDate',
                    event.target.checked,
                  )
                }
                size="small"
              />
            }
            label="Sin fecha de vencimiento"
            componentsProps={{
              typography: {
                variant: 'caption',
                fontSize: 10,
              },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Subscription.DataRetentionMin.DatePicker
            label="Desde"
            value={filtersInWarehouse.filters.expirationDateTimeFrom}
            onChange={handleChangeInputDateFilter(filtersInWarehouse)(
              'expirationDateTimeFrom',
            )}
            renderInput={(props) => <TextField {...props} />}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <Subscription.DataRetentionMin.DatePicker
            label="Hasta"
            value={filtersInWarehouse.filters.expirationDateTimeTo}
            onChange={handleChangeInputDateFilter(filtersInWarehouse)(
              'expirationDateTimeTo',
            )}
            renderInput={(props) => <TextField {...props} />}
          />
        </Stack>
      </Stack>

      {/* <CustomAutocomplete
        innerRef={filtersInWarehouse.autocompletesRefs.itemState}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              label={val.label.toString()}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={IN_WAREHOUSE_STATE_OPTIONS}
        label="Estados"
        name="itemState"
        onChange={(_e, vals) => {
          filtersInWarehouse.onChangeFilterInput('itemState', vals);
        }}
        fullWidth
        getOptionLabel={(option) => option.label.toString()}
      /> */}

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.LimitPremium}
        innerRef={filtersInWarehouse.autocompletesRefs.pickingState}
        options={PICKING_STATE_OPTIONS}
        label="Estado de picking"
        name="pickingState"
        onChange={(_e, val) => {
          filtersInWarehouse.onChangeFilterInput('pickingState', val);
        }}
        fullWidth
        getOptionLabel={(option) => option.label.toString()}
      />

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.LimitNotPro}
        innerRef={filtersInWarehouse.autocompletesRefs.services}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              label={val.label.toString()}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={selectsData.services.list}
        loading={selectsData.services.loading}
        label="Tipos de servicios"
        name="services"
        onChange={(_e, vals) => {
          filtersInWarehouse.onChangeFilterInput('services', vals);
        }}
        fullWidth
        getOptionLabel={(option) => option.label.toString()}
      />

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.MgmZones}
        innerRef={filtersInWarehouse.autocompletesRefs.logisticsZones}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              icon={
                val.color ? (
                  <FontAwesomeIcon
                    icon={LogisticsZonesIcon}
                    style={{ color: val.color }}
                  />
                ) : undefined
              }
              label={val.code}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={selectsData.logisticsZones.list}
        loading={selectsData.logisticsZones.loading}
        label="Zonas logísticas"
        name="zones"
        onChange={(_e, vals) => {
          filtersInWarehouse.onChangeFilterInput('zones', vals);
        }}
        getOptionLabel={(option) =>
          option.description
            ? `${option.code} ${option.description}`
            : option.code
        }
        renderOption={(props, option) => (
          <li {...props}>
            <LogisiticZoneInfo
              name={option.code}
              description={option.description}
              color={option.color}
              withTooltip={Boolean(option.color)}
            />
          </li>
        )}
        fullWidth
      />

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.LimitNotPro}
        innerRef={filtersInWarehouse.autocompletesRefs.assignedUser}
        options={selectsData.assignedUser.list}
        loading={selectsData.assignedUser.loading}
        label="Usuario asignado"
        name="assignedUser"
        onChange={(_e, val) => {
          filtersInWarehouse.onChangeFilterInput('assignedUser', val);
        }}
        fullWidth
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.userId}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <UserInfo
              name={`${option.lastName}, ${option.firstName}`}
              urlAvatar={option.avatarUrl || undefined}
            />
          </li>
        )}
        startAdornment={(val) =>
          val && (
            <Avatar
              src={getDefaultAvatar(val.avatarUrl || undefined)}
              alt={`${val.lastName}, ${val.firstName}`}
              sx={{ width: 20, height: 20 }}
            />
          )
        }
        groupBy={(option) => {
          if (option.roleId === Roles.Drive) {
            return 'Choferes';
          }

          return 'Usuarios';
        }}
        filterOptions={createFilterOptions({
          ignoreAccents: true,
          ignoreCase: true,
          stringify: (option) =>
            `${option.lastName}, ${option.firstName}`.replace(',', ''),
        })}
      />

      <CustomAutocomplete
        innerRef={filtersInWarehouse.autocompletesRefs.sourceImports}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              avatar={
                <Box
                  component="img"
                  src={val.image.url}
                  alt={val.image.alt}
                  sx={{ height: 18, width: 18, objectFit: 'contain' }}
                />
              }
              label={val.label}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={SOURCE_IMPORT_TYPES_OPTIONS}
        label="Medios de importación"
        name="sourceImports"
        onChange={(_e, vals) => {
          filtersInWarehouse.onChangeFilterInput('sourceImports', vals);
        }}
        getOptionLabel={(option) => option.label.toString()}
        renderOption={(props, option) => (
          <li {...props}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                component="img"
                src={option.image.url}
                alt={option.image.alt}
                sx={{ height: 25, width: 25, objectFit: 'contain' }}
              />

              <Typography sx={{ fontSize: 14 }}>{option.label}</Typography>
            </Stack>
          </li>
        )}
      />
    </OrdersFiltersMenuV2Inputs>
  );
};

export default OrdersFiltersMenuV2VariantInWarehouse;
